import React, { useState, useEffect, useMemo } from "react";
import { getSectionShape } from "../utils";

const SectionSettingsSidebar = ({
  selectedSection,
  sectionSize,
  setSectionSize,
  seatDimensions,
  setSeatDimensions,
  selectedSectionSeatLimit,
  handleDeleteSection,
  setIsSectionModalOpen,
  handleUpdateSection,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");

  useEffect(() => {
    if (selectedSection) {
      setEditName(selectedSection.name);
      setIsEditing(false);
    }
  }, [selectedSection]);

  // Identify seats that would be orphaned with the current dimension values
  const orphanedSeatsWarning = useMemo(() => {
    if (!selectedSection || !isEditing) return null;
    const newRows = Number(seatDimensions.rows) || 2;
    const newCols = Number(seatDimensions.columns) || 6;
    const orphaned = selectedSection.seats.filter(
      (seat) => seat.row > newRows || seat.column > newCols
    );
    if (orphaned.length === 0) return null;
    return {
      count: orphaned.length,
      labels: orphaned.map((s) => s.label).filter(Boolean),
    };
  }, [selectedSection, isEditing, seatDimensions.rows, seatDimensions.columns]);

  const onSave = async (e) => {
    e.preventDefault();
    if (!selectedSection) return;
    
    await handleUpdateSection({
      id: selectedSection.id,
      name: editName,
      type: selectedSection.type,
      width: Number(sectionSize.width),
      height: Number(sectionSize.height),
      seatRows: Number(seatDimensions.rows),
      seatColumns: Number(seatDimensions.columns),
      x: selectedSection.x,
      y: selectedSection.y,
    });
    setIsEditing(false);
  };

  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm h-full flex flex-col">
      <div className="mb-6">
        <h2 className="text-lg font-bold text-slate-800">
          Section Details
        </h2>
        <p className="text-xs text-slate-400 mt-1">Manage section properties and seats</p>
      </div>

      {!selectedSection ? (
        <div className="mt-2 rounded-2xl bg-slate-50 border border-dashed border-slate-200 p-8 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
          </div>
          <p className="text-sm font-medium text-slate-500">No section selected</p>
          <p className="mt-1 text-xs text-slate-400">Click a section on the floor plan to view details.</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 min-w-0">
                {!isEditing ? (
                  <h3 className="text-base font-bold text-slate-800 truncate">{selectedSection.name}</h3>
                ) : (
                  <input
                    type="text"
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full rounded-lg border border-indigo-200 px-3 py-1.5 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-indigo-100 bg-white"
                    placeholder="Section name"
                  />
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-slate-50 p-3 border border-slate-100">
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-1">Position</span>
                <span className="text-xs font-bold text-slate-700">C{selectedSection.x}, R{selectedSection.y}</span>
              </div>
              <div className="rounded-2xl bg-slate-50 p-3 border border-slate-100">
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-1">Capacity</span>
                <span className="text-xs font-bold text-indigo-600">{selectedSection.seats.length} / {selectedSectionSeatLimit}</span>
              </div>
            </div>

            {/* Seat Dimensions Display (read-only) */}
            {!isEditing && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="rounded-2xl bg-slate-50 p-3 border border-slate-100">
                  <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-1">Seat Rows</span>
                  <span className="text-xs font-bold text-slate-700">{selectedSection.seatRows || 2}</span>
                </div>
                <div className="rounded-2xl bg-slate-50 p-3 border border-slate-100">
                  <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-1">Seat Columns</span>
                  <span className="text-xs font-bold text-slate-700">{selectedSection.seatColumns || 6}</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex-1">
            {isEditing ? (
              <form onSubmit={onSave} className="space-y-6">
                <div>
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    DISPLAY DIMENSIONS
                  </label>
                  <div className="grid grid-cols-2 gap-3 items-center relative">
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        value={sectionSize.width}
                        onChange={(event) =>
                          setSectionSize((current) => ({
                            ...current,
                            width: event.target.value,
                          }))
                        }
                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-center text-sm font-bold text-slate-800 outline-none focus:border-indigo-300 bg-slate-50"
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-300">W</span>
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        value={sectionSize.height}
                        onChange={(event) =>
                          setSectionSize((current) => ({
                            ...current,
                            height: event.target.value,
                          }))
                        }
                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-center text-sm font-bold text-slate-800 outline-none focus:border-indigo-300 bg-slate-50"
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-300">H</span>
                    </div>
                  </div>
                  <p className="mt-2 text-[10px] text-slate-400 leading-relaxed">
                    Adjust how the section appears on the grid. This doesn't affect seat capacity.
                  </p>
                </div>

                {/* Seat Dimensions (Editable) */}
                <div>
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    SEAT LAYOUT DIMENSIONS
                  </label>
                  <div className="grid grid-cols-2 gap-3 items-center relative">
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={seatDimensions.rows}
                        onChange={(event) =>
                          setSeatDimensions((current) => ({
                            ...current,
                            rows: event.target.value,
                          }))
                        }
                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-center text-sm font-bold text-slate-800 outline-none focus:border-indigo-300 bg-slate-50"
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-300">R</span>
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={seatDimensions.columns}
                        onChange={(event) =>
                          setSeatDimensions((current) => ({
                            ...current,
                            columns: event.target.value,
                          }))
                        }
                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-center text-sm font-bold text-slate-800 outline-none focus:border-indigo-300 bg-slate-50"
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-300">C</span>
                    </div>
                  </div>
                  <p className="mt-2 text-[10px] text-slate-400 leading-relaxed">
                    Configure the seat grid layout (rows × columns). Total seat capacity = {(Number(seatDimensions.rows) || 0) * (Number(seatDimensions.columns) || 0)} slots.
                  </p>

                  {/* Warning about seats that will be removed */}
                  {orphanedSeatsWarning && (
                    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
                      <div className="flex items-start gap-2">
                        <svg className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                          <line x1="12" y1="9" x2="12" y2="13" />
                          <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                        <div>
                          <p className="text-[10px] font-bold text-amber-700">
                            {orphanedSeatsWarning.count} seat(s) will be removed
                          </p>
                          {orphanedSeatsWarning.labels.length > 0 && (
                            <p className="mt-0.5 text-[10px] text-amber-600">
                              {orphanedSeatsWarning.labels.join(", ")}
                            </p>
                          )}
                          <p className="mt-0.5 text-[10px] text-amber-500">
                            These seats fall outside the new grid and will be deleted on save.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-600 transition hover:bg-slate-50 active:scale-95"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm shadow-indigo-100 transition hover:bg-indigo-700 active:scale-95"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setIsSectionModalOpen("seats")}
                  className="w-full group flex items-center justify-between rounded-2xl bg-[#1e1e1e] px-5 py-4 text-sm font-bold text-white transition hover:bg-black active:scale-[0.98]"
                >
                  <div className="flex items-center gap-2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M9 3v18" /><path d="M15 3v18" /><path d="M3 9h18" /><path d="M3 15h18" /></svg>
                    Manage Seats
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-slate-200 group-hover:translate-x-1 transition-transform"><path d="m9 18 6-6-6-6"/></svg>
                </button>

                <button
                  type="button"
                  onClick={() => setIsSectionModalOpen("bookings")}
                  className="w-full group flex items-center justify-between rounded-2xl bg-[#1e1e1e] px-5 py-4 text-sm font-bold text-white transition hover:bg-black active:scale-[0.98]"
                >
                  <div className="flex items-center gap-2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                    Manage Bookings
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-slate-200 group-hover:translate-x-1 transition-transform"><path d="m9 18 6-6-6-6"/></svg>
                </button>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs font-bold text-slate-600 transition hover:bg-slate-50 hover:border-slate-300 active:scale-[0.97]"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    Edit Section
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteSection}
                    className="flex items-center justify-center gap-2 rounded-xl border border-rose-100 bg-rose-50/30 px-2 py-3 text-xs font-bold text-rose-500 transition hover:bg-rose-50 hover:border-rose-200 active:scale-[0.97]"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                    Delete Section
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionSettingsSidebar;
